import { DecimalPipe, formatNumber } from "@angular/common";
import { DateTime, DateTimeFormatOptions, LocaleOptions } from "luxon";

/**
 * Interface of a client side application exception
 *
 * @export
 * @interface ICoreApplicationError
 */
export interface ICoreApplicationError {
    /**
     * Error message
     *
     * @type {string}
     * @memberof ICoreApplicationError
     */
    message: string;
    /**
     * Error stacktrace
     *
     * @type {string}
     * @memberof ICoreApplicationError
     */
    stack: string;
    /**
     * Path where error occurred
     *
     * @type {string}
     * @memberof ICoreApplicationError
     */
    path: string;
    /**
     * User
     *
     * @type {string}
     * @memberof ICoreApplicationError
     */
    username: string;
    /**
     * Error additional information as JSON string
     *
     * @type {string}
     * @memberof ICoreApplicationError
     */
    info: string;
}

export type EidosBootstrapColWidth = '-' | '-xs-' | '-sm-' | '-md-' | '-lg-' | '-xl-';
export type EidosBootstrapColumns = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export type EidosBootstrapAlias = `col${EidosBootstrapColWidth}${EidosBootstrapColumns}`;

/**
 * Eidos menu possible sort status
 *
 * @export
 * @enum {number}
 */
export enum EidosMenuOrderStatus {
    Unset = 0,
    AZ = 1,
    ZA = 2
}

/**
 * Keys enum of info saved in local storage
 *
 * @export
 * @enum {string}
 */
export const enum LocalStorageKeys {
    USER = 'user',
    RAW_USER = 'raw_user',
    TOKEN = 'mybiz_token',
    REQUESTED_ROUTE = 'requested_route',
    PINNED_MENU = 'pinned_menu',
    DEBUG_MODE = 'debug_mode',
    TRAVELSTUDIO_TOKEN='TravelStudio_Token',
}

/**
 * Keys enum of info saved in session storage
 *
 * @export
 * @enum {number}
 */
export enum SessionStorageKeys {
    OBJECT_DEFINITION = 'object_definition_{id}',
    OBJECT_DATA = 'object_data_{id}_page{page}'
}

/**
 * Eidos security check request type
 *
 * @export
 * @enum {number}
 */
export enum SecurityCheckRequestType {
    User,
    Authentication
}

/**
 * Eidos icon types enum
 *
 * @export
 * @enum {string}
 */
export enum EidosIconType {
    MyBiz = 'image',
    Material = 'material',
    Awesome = 'awesome',
    SVG = 'svg',
    DX = 'dx'
}

/**
 * Type of event messages from child iframe enum
 *
 * @export
 * @enum {string}
 */
export enum EidosIframeBehaviourType {
    Dataloaded = 'dataloaded',
    RefreshToken = 'refresh_token',
    LoginTokenError = 'login_token_error',
    LoginTokenSuccessful = 'login_token_successful',
    Route = 'route',
    Pong = 'pong'
}

/**
 * Type of post messages from child iframe enum
 *
 * @export
 * @enum {number}
 */
export enum EidosIframeEventType {
    Command = "command",
    Version = "version",
    Event = "event"
}

/**
 * Core routes enum
 *
 * @export
 * @enum {string}
 */
export enum CoreRoute {
    ResetCredentials = 'reset',
    Login = 'login',
    Home = 'entity-master',
    // ExternalApp = '/eidos/app',
    Readme = 'readme',
    NotFound = 'error/404',
    RegisterAccount = "/account/register"
}

/**
 * Types of profile menu action enumerations
 *
 * @export
 * @enum {string}
 */
export enum MenuActionType {
    Impersonate = 'impersonate',
    StopImpersonate = 'stopImpersonate',
    Settings = 'settings',
    ChangePassword = 'changePassword',
    Logout = 'logout',
    Debug = 'debugMode',
    Refresh = 'refresh',
    Readme = 'readme'
}

/**
 * Types of Eidos debug log severity enum
 *
 * @export
 * @enum {number}
 */
export enum EidosLogSeverity {
    Log,
    Warning,
    Error
}

/**
 * Eidos external app action types
 *
 * @export
 * @enum {number}
 */
export enum EidosExternalAppAction {
    Eidos = 'eobject',
    MyBiz = 'object',
    EmbeddedUrl = 'eurl'
}

/**
 * if input value is a valid ISO datetime, casts input to format string provided
 * If no format is provided, casts it to dd/mm/yyyy if date is at midnight, dd/mm/yyyy hh:mm otherwise
 *
 * @export
 * @param {*} value
 * @param {string} [format]
 * @return {*}  {string}
 */
export function eventuallyDisplayDateAndTime(value: any, format?: string): string {
    value = value.toString();

    const castedTime = castToDateTime(value);
    if (castedTime) {
        const defaultFormat = castedTime.toMillis() === castedTime.startOf('day').toMillis() ? 'dd/LL/yyyy' : 'dd/LL/yyyy HH:mm';
        return castedTime.toFormat(format ?? defaultFormat);
    }

    return value;
}


export function castToDateTime(value: any): DateTime | undefined {
    value = value.toString();

    const timeFromIso = DateTime.fromISO(value);
    if (timeFromIso.isValid) {
        return timeFromIso;
    } else {
        const timefromRFC2822 = DateTime.fromRFC2822(value);
        return timefromRFC2822.isValid ? timefromRFC2822 : undefined;
    }
}

export enum EidosAuthType {
    Form = 'F',
    Windows = 'W',
    Microsoft = 'M',
    Google = 'G'
}

export class EidosStandardFormat {
    static decimalPipe = new DecimalPipe('en');
    static defaultCurrency(): string { return 'USD'; }
    static DateAndTimeFmt(): string { return 'dd/MM/yyyy HH:mm'; }
    static DateQueryString(): string { return 'yyyy-MM-dd'; }
    static DateFmtBase(): string { return 'dd/MM/yyyy'; }
    static DateFmtWithMonthName(): string { return 'dd MMM yyyy'; }
    static DateFmtWithMonthNoYear(): string { return 'dd MMM'; }

    static GetDateStandardLocaleOpts(): LocaleOptions { return { locale: 'en' }; };
    static DateFmtOptsDayNameMonthStartYearDay(): DateTimeFormatOptions { return { weekday: 'long', day: '2-digit', month: 'short', year: '2-digit' }; };
    static TimeFmtOptsHourMinuteAMPM(): DateTimeFormatOptions { return DateTime.TIME_SIMPLE; };

    static getCurrencySymbol(currency: string): string {
        switch (currency.trim().toUpperCase()) {
            case 'CAD':
                return 'C$';
            case 'AUD':
                return 'A$';
            case 'EUR':
                return '€';
            case 'GBP':
                return '£';
            case 'USD':
                return '$';
            default:
                return currency;
        }
    }
    static CurrencyAmount(amount: number, currency?: string): string { return EidosStandardFormat.getCurrencySymbol(currency || EidosStandardFormat.defaultCurrency()) + ' ' + EidosStandardFormat.decimalPipe.transform(amount, '1.2-2', 'en'); }
    static CurrencyAmountInverted(amount: number, currency?: string): string { return EidosStandardFormat.decimalPipe.transform(amount, '1.2-2', 'en') + ' ' + EidosStandardFormat.getCurrencySymbol(currency || EidosStandardFormat.defaultCurrency()); }
    static PercentualNumber(value: number): string { return EidosStandardFormat.decimalPipe.transform(value, '1.0-2', 'en') + ' %'; }
}
/*******************************
 * Funzioni x ECM
 *******************************/

/**
 * Checks if browser is IE or EDGE
 */
export const isIE = () => {
    return /(Edge|MSIE|Trident\/7\.0.*rv\:([0-9\.]+).*\).*Gecko$)/i.test(navigator.userAgent);
};


//import { isIE } from './client';

const XLS_FORMAT = '.xls';
const XLSX_FORMAT = '.xlsx';
const PNG_FORMAT = '.png';
const JPEG_FORMAT = '.jpeg';
const JPG_FORMAT = '.jpg';
const GIF_FORMAT = '.gif';
const MEDIA_TYPE_XLS = 'application/vnd.ms-excel';
const MEDIA_TYPE_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const MEDIA_TYPE_PNG = 'image/png';
const MEDIA_TYPE_JPEG = 'image/jpeg';
const MEDIA_TYPE_GIF = 'image/gif';
const DEFAULT_TYPE = 'text/plain';

const getMediaType = (fileName: string) => {
    if (fileName) {
        if (fileName.indexOf(XLSX_FORMAT) > -1) {
            return MEDIA_TYPE_XLSX;
        } else if (fileName.indexOf(XLS_FORMAT) > -1) {
            return MEDIA_TYPE_XLS;
        } else if (fileName.indexOf(PNG_FORMAT) > -1) {
            return MEDIA_TYPE_PNG;
        } else if (fileName.indexOf(JPEG_FORMAT) > -1) {
            return MEDIA_TYPE_JPEG;
        } else if (fileName.indexOf(JPG_FORMAT) > -1) {
            return MEDIA_TYPE_JPEG;
        } else if (fileName.indexOf(GIF_FORMAT) > -1) {
            return MEDIA_TYPE_GIF;
        } else {
            return DEFAULT_TYPE;
        }
    } else {
        return '';
    }
};


const streamToBlob = (stream: string, mediaType: string = '', sliceSize: number = 512) => {
    const byteCharacters = atob(stream);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: mediaType });
    return blob;
};


export const fileToBase64 = (file: File) => {
    return new Promise(resolve => {
        const reader = new FileReader();

        // Convert data to base64
        reader.readAsDataURL(file);

        // Read file content on file loaded event
        reader.onload = (event: any) => {
            resolve(event.target.result);
        };
    });
};


export const base64ToStream = (base64String: string) => {
    return base64String ? base64String.substr(base64String.indexOf('base64,') + 7) : null;
};

export const streamToBase64 = (fileName: string, stream: string) => {
    const mediaType = getMediaType(fileName);
    return stream ? `data:${mediaType};base64,${stream}` : null;
};


export const downloadFromBase64 = (fileToDownloadBase64: string, fileName: string) => {

      // Tanto IE non è più supportato
      /*
    if (isIE()) {

      // Create blob
      const stream = base64ToStream(fileToDownloadBase64);
      const mediaType = getMediaType(fileName);

      const blob = streamToBlob(stream, mediaType);

      // Start download
      window.navigator.msSaveBlob(blob, fileName);

    } else
    */{

        // Create 'a' element with href = fileToDownloadBase64
        const a = document.createElement('a');
        a.setAttribute('href', fileToDownloadBase64);
        a.setAttribute('download', fileName);
        a.style.display = 'none';
        document.body.appendChild(a);

        // Trigger click event (start download)
        a.click();

        // Remove 'a' element
        document.body.removeChild(a);

    }

};

